import { Divider, MenuItem, useTheme } from '@mui/material';
import { borderRadius, Box, padding } from '@mui/system';
import { Iconify, MenuPopover, NextImage } from '@sdf/base';
import { useCallback, useState } from 'react';
import { DeleteConfirmModal } from '../delete-confirm/DeleteConfirmModal';
import { SharingVideoModal } from '../sharing-video-modal/SharingVideoModal';
import { useSnackbar } from 'notistack';

import {
  API_CONSTANTS,
  BrollRenderStatus,
  CardTypeEnum,
  extractObjectId,
  MediaStatus,
  MediaStatusType,
} from '../../utils';
import { useHandleApiCall, useNotification } from '../../hooks';
import { Styles } from '@t2c/shared-fe';
import { useRouter } from 'next/router';
import { RenameDialog } from '../text-to-speech/RenameDialog';

interface MenuListProps {
  anchorEl: null | HTMLElement;
  handleMenuListClose: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  handleShareVideo: () => void;
  handleDelete: () => void;
  handleDownload: () => void;
  handleRename: () => void;
  handleCloneVideo: () => void;
  isBrollCaptionVideo?: boolean;
  isAvatarCreation?: boolean;
  handleBrollCaptionVideoView?: Function;
  handleBrollCaptionVideoPreview?: Function;
  brollRenderStatus?: BrollRenderStatus;
  status: MediaStatusType;
  handleTalkingFacePreview: () => void;
}

const MenuList: React.FC<MenuListProps> = ({
  anchorEl,
  handleMenuListClose,
  handleShareVideo,
  handleDelete,
  handleDownload,
  handleRename,
  handleCloneVideo,
  isBrollCaptionVideo,
  isAvatarCreation,
  handleBrollCaptionVideoView,
  handleBrollCaptionVideoPreview,
  brollRenderStatus,
  status,
  handleTalkingFacePreview,
}) => {
  const open = Boolean(anchorEl);

  return (
    <MenuPopover
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuListClose}
      disabledArrow
      sx={styles.menu}
    >
      {isBrollCaptionVideo ? (
        <>
          {status === MediaStatus.Success && brollRenderStatus !== BrollRenderStatus.Error && (
            <>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleBrollCaptionVideoView();
                }}
                sx={styles.firtsMenuItemStyle}
              >
                <Iconify icon={'mdi:format-color-fill'} sx={styles.menuIcons} />
                Broll / Caption
              </MenuItem>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleCloneVideo();
                }}
                sx={styles.lastMenuItemStyle}
              >
                <Iconify icon={'material-symbols:content-copy'} sx={styles.menuIcons} />
                Clone
              </MenuItem>
            </>
          )}
          {brollRenderStatus === BrollRenderStatus.Success && (
            <>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownload();
                }}
                sx={styles.menuItemStyle}
              >
                <Iconify icon={'material-symbols:download'} sx={styles.menuIcons} />
                Download
              </MenuItem>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleBrollCaptionVideoPreview();
                }}
                sx={styles.menuItemStyle}
              >
                <Iconify icon={'mdi:eye-outline'} sx={styles.menuIcons} />
                Preview
              </MenuItem>
            </>
          )}
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleDelete();
            }}
            sx={styles.lastMenuItemStyle}
          >
            <Iconify icon={'material-symbols:delete'} sx={styles.menuIcons} />
            Delete
          </MenuItem>
        </>
      ) : (
        <>
          {status === MediaStatus.Success && !isAvatarCreation && (
            <>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleTalkingFacePreview();
                }}
                sx={styles.firtsMenuItemStyle}
              >
                <Iconify icon={'mdi:eye-outline'} sx={styles.menuIcons} />
                Preview
              </MenuItem>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleRename();
                }}
                sx={styles.firtsMenuItemStyle}
              >
                <Iconify icon={'mdi:rename'} sx={styles.menuIcons} />
                Rename
              </MenuItem>
              <Divider sx={{ margin: '0px !important' }} />
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleShareVideo();
                }}
                sx={styles.menuItemStyle}
              >
                <Iconify icon={'material-symbols:share'} sx={styles.menuIcons} />
                Share Video
              </MenuItem>
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownload();
                }}
                sx={styles.menuItemStyle}
              >
                <Iconify icon={'material-symbols:download'} sx={styles.menuIcons} />
                Download
              </MenuItem>
            </>
          )}
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleDelete();
            }}
            sx={styles.lastMenuItemStyle}
          >
            <Iconify icon={'material-symbols:delete'} sx={styles.menuIcons} />
            Delete
          </MenuItem>
        </>
      )}
    </MenuPopover>
  );
};

export const DropDownOptions = ({
  id,
  name,
  url,
  isBrollCaptionVideo,
  isAvatarCreation,
  handleBrollCaptionVideoPreview,
  brollRenderStatus,
  refetchData,
  status,
  type,
}: {
  id: string;
  name: string;
  url: string;
  isBrollCaptionVideo?: boolean;
  isAvatarCreation?: boolean;
  handleBrollCaptionVideoPreview?: Function;
  brollRenderStatus?: BrollRenderStatus;
  refetchData?: Function;
  status: MediaStatusType;
  type?: CardTypeEnum;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
  const [openRenameModal, setOpenRenameModal] = useState<boolean>(false);
  const [isEditNameLoading, setIsEditNameLoading] = useState<boolean>(false);
  const { post, patch } = useHandleApiCall();
  const { enqueueSnackbar } = useSnackbar();
  const { showNotification } = useNotification();
  const [rename, setRename] = useState<string>(name);

  const updateVideoMutatuion = patch(
    API_CONSTANTS.UPDATE_VIDEO.key,
    `${API_CONSTANTS.UPDATE_VIDEO.path}/${id}`
  );

  const getPlayableUrlMutation = post(
    API_CONSTANTS.GET_PRESIGNED_URL.key,
    API_CONSTANTS.GET_PRESIGNED_URL.path
  );

  const cloneVideoMutation = post(API_CONSTANTS.CLONE_VIDEO.key, API_CONSTANTS.CLONE_VIDEO.path);

  const getPlayableUrl = useCallback(
    async (objectId: string) => {
      try {
        const data = await getPlayableUrlMutation.mutateAsync({
          format: 'video',
          objectId: objectId,
          name: `${name}.mp4`,
        });
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    [name]
  );
  const handleShareVideo = useCallback(() => {
    setIsOpen((prevValue) => !prevValue);
    setAnchorEl(null);
  }, []);

  const handleMenuListClose = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    setAnchorEl(null);
  }, []);

  const handleMenuListClick = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleDeleteItem = () => {
    setIsDeleteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmOpen(false);
  };

  const handleCloneVideo = async () => {
    try {
      setAnchorEl(null);
      enqueueSnackbar('Cloning in Progress...', { variant: 'info' });
      const response = await cloneVideoMutation.mutateAsync({ videoId: id });
      console.log('Clone response:', response);
      enqueueSnackbar('Clone successful!', { variant: 'success' });

      // Optionally, refetch data or update UI after successful cloning
      if (refetchData) {
        refetchData();
      }
    } catch (error) {
      console.error('Error cloning video:', error);
    }
  };

  const handleDownload = async () => {
    const link = document.createElement('a');
    const objectId = extractObjectId(url);
    const playableUrl = await getPlayableUrl(objectId);
    link.href = playableUrl;
    link.download = 'filename.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setAnchorEl(null);
  };

  const handleCloseRenameModal = () => setOpenRenameModal(false);
  const handleRename = () => {
    setOpenRenameModal(true);
    setAnchorEl(null);
  };
  const theme = useTheme();
  const { push } = useRouter();
  const handleBrollCaptionVideoView = () => {
    push(`/edit-video/${id}`);
  };

  const handleTalkingFacePreview = () => {
    push(`/video/${id}`);
  };

  const handleSubmitRename = async (name: string) => {
    try {
      setIsEditNameLoading(true);
      await updateVideoMutatuion.mutateAsync({
        name: name.trim(),
      });
      refetchData();
      showNotification('Video Name Updated!', 'success');
      handleCloseRenameModal();
      setRename(rename.trim());
    } catch (err) {
      showNotification(err?.message || 'Failed to update name. Please try again.', 'error');
      console.error(err);
    }
    setIsEditNameLoading(false);
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        <RenameDialog
          shouldOpenDialog={openRenameModal}
          handleCloseDialog={handleCloseRenameModal}
          initialName={name || ''}
          handleOnRename={handleSubmitRename}
          isLoading={isEditNameLoading}
          dialogTitle="Rename"
        />
      </div>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          borderRadius: '50%',
          padding: '2px',
          marginRight: '10px',
        }}
        onClick={handleMenuListClick}
      >
        <MenuList
          anchorEl={anchorEl}
          handleMenuListClose={handleMenuListClose}
          handleShareVideo={handleShareVideo}
          handleDelete={handleDeleteItem}
          handleDownload={handleDownload}
          handleRename={handleRename}
          handleCloneVideo={handleCloneVideo}
          isBrollCaptionVideo={isBrollCaptionVideo}
          isAvatarCreation={isAvatarCreation}
          handleBrollCaptionVideoView={handleBrollCaptionVideoView}
          handleBrollCaptionVideoPreview={handleBrollCaptionVideoPreview}
          brollRenderStatus={brollRenderStatus}
          status={status}
          handleTalkingFacePreview={handleTalkingFacePreview}
        />
        <Iconify icon="solar:menu-dots-bold" fontSize={'22px'} color={theme.palette.text.primary} />
      </Box>

      <SharingVideoModal
        handleCloseSharingModal={handleShareVideo}
        videoId={id}
        open={isOpen}
        url={url}
      />
      <DeleteConfirmModal
        handleCancel={handleCancelDelete}
        open={isDeleteConfirmOpen}
        id={id}
        name={name}
        refetchData={refetchData}
        type={type}
      />
    </>
  );
};

const menuItemStyle = {
  color: (theme) => theme.palette.text.primary,
  fontSize: '12px',
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 0,
  padding: 1,
  width: '100%',
  '&:hover': {
    backgroundColor: (theme) => theme.palette.background.activeCard,
  },
};
const styles: Styles = {
  menu: {
    maxWidth: '147px',
    borderRadius: '8px',
    padding: 0,
    backgroundColor: (theme) => theme.palette.background.primary,
  },
  menuItemStyle: {
    ...menuItemStyle,
  },
  firtsMenuItemStyle: {
    ...menuItemStyle,
    borderTopRightRadius: '8px',
    borderTopLeftRadius: '8px',
  },
  lastMenuItemStyle: {
    ...menuItemStyle,
    borderBottomRightRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  menuIcons: {
    width: 12,
    height: 12,
    marginRight: '6px',
  },
};
